<template>
	<!-- NOTE: This dialog is a full screen dialog, otherwise we must use the Dialog component from the Dialog.vue file. -->
	<v-dialog
		v-model="isVisible"
		@cancel="closeDialog"
		fullscreen
		hide-overlay
		transition="dialog-bottom-transition"
		>
			<div class="w-100 h-100 pa-2 d-flex flex-column overflow-hidden" style="background-color: white; max-height: 100vh; ">
				<!-- Header with integrated controls -->
				<div class="d-flex align-center w-100 pa-2 flex-shrink-0 rounded rounded-b-0" style="background-color: #e8e8e8;">
					<!-- Left Section -->
					<div class="d-flex align-center ga-1 overflow-hidden" style="flex: 1;">
						<v-icon>icon-file-blank-solid</v-icon>
						<h5 v-test="'pdf-viewer-title'" class="text-truncate">{{ fileName }}</h5>
						<v-btn
							icon
							:disabled="!pdfUrl"
							@click="downloadPDF"
							v-test="'pdf-viewer-download'">
							<v-icon>icon-download</v-icon>
						</v-btn>
					</div>
					
					<!-- Center Section - Page Controls -->
					<div v-if="pdfLoaded" class="d-flex align-center justify-center" style="flex: 0 0 auto;">
						<div class="d-flex align-center ga-1">
							<!-- Page Navigation Controls -->
							<v-btn
								icon
								small
								:disabled="currentPage <= 1"
								@click="goToPreviousPage"
								v-test="'pdf-viewer-prev-page'">
								<v-icon>icon-chevron-left</v-icon>
							</v-btn>
							<v-text-field
								v-model.number="pageInput"
								@keyup.enter="goToPage"
								@change="goToPage"
								@input="goToPage"
								dense
								outlined
								hide-details="true"
								type="number"
								:min="1"
								:max="numPages"
								class="text-center mb-0">
							</v-text-field>
							<span class="text-body-1 text-no-wrap">of {{ numPages }}</span>
							<v-btn
								icon
								small
								:disabled="currentPage >= numPages"
								@click="goToNextPage"
								v-test="'pdf-viewer-next-page'">
								<v-icon>icon-chevron-right</v-icon>
							</v-btn>
						</div>
					</div>
					
					<!-- Right Section -->
					<div class="d-flex justify-end" style="flex: 1;">
						<v-btn
							icon
							@click="closeDialog"
							v-test="'pdf-viewer-close-button'">
								<v-icon>icon-close-large</v-icon>
						</v-btn>
					</div>
				</div>
				<div class="d-flex flex-column flex-grow-1" style="min-height: 0;">
					<div v-if="loading" class="d-flex justify-center align-center flex-grow-1">
						<v-progress-circular
							indeterminate
							color="primary"
							size="64">
						</v-progress-circular>
						<span class="ml-4">Loading PDF...</span>
					</div>
					<div v-else-if="error" class="d-flex flex-column justify-center align-center flex-grow-1">
						<v-icon color="error" size="64" class="mb-4">icon-exclamation-triangle</v-icon>
						<h3 class="mb-2">{{ errorTitle }}</h3>
						<p class="text-center text--secondary mb-4">{{ error }}</p>
						<v-btn
							color="primary"
							@click="loadPDF"
							v-if="errorSupportsRetries"
							v-test="'pdf-viewer-retry-button'">
							<v-icon left>icon-refresh</v-icon>
							Retry
						</v-btn>
					</div>
					<div v-if="pdfLoaded" class="d-flex flex-column flex-grow-1 relative" style="min-height: 0;">
						<!-- PDF Canvas Container (removed separate controls section) -->
						<div 
							class="pdf-canvas-container flex-grow-1 rounded rounded-t-0 overflow-auto relative" 
							ref="canvasContainer" 
							style="min-height: 0;"
							@touchstart="handleTouchStart"
							@touchmove="handleTouchMove"
							@touchend="handleTouchEnd">
							<div class="canvas-wrapper d-flex justify-center align-center">
								<canvas ref="pdfCanvas" class="pdf-canvas" v-show="hasInitialRender"></canvas>
							</div>
						</div>
						
						<!-- Floating Zoom Controls - moved outside scrolling container -->
						<div class="floating-zoom-controls ga-1 d-flex flex-column justify-center align-center right-4 bottom-4 absolute z-2">
							<v-btn
								fab
								small
								color="white"
								elevation="2"
								class="mb-2 zoom-btn"
								:disabled="zoomLevel >= 3"
								@click="zoomIn"
								v-test="'pdf-viewer-zoom-in'">
								<v-icon>icon-plus-circle-stroke</v-icon>
							</v-btn>
							<v-btn
								fab
								small
								color="white"
								elevation="2"
								class="zoom-btn"
								:disabled="zoomLevel <= 0.5"
								@click="zoomOut"
								v-test="'pdf-viewer-zoom-out'">
								<v-icon>icon-minus-circle-stroke</v-icon>
							</v-btn>
						</div>
					</div>
				</div>
		 </div>
	</v-dialog>
</template>

<script>
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import { downloadFile } from '@/helpers/download';
import * as Sentry from '@sentry/vue';
import packageJson from '../../package.json';
import "core-js/stable/url"; // Safari URL polyfill
import "core-js/proposals/promise-with-resolvers"; // Safari 15.4 Promise polyfill

// Set PDF.js worker source dynamically based on package.json version
GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${packageJson.dependencies['pdfjs-dist']}/build/pdf.worker.min.mjs`;

export default {
	name: 'PDFViewerDialog',
	components: {
	},
	props: {
		visible: {
			type: Boolean,
			default: false
		},
		pdfUrl: {
			type: String,
			required: false
		},
		fileName: {
			type: String,
			default: ''
		},
	},
	data() {
		return {
			isVisible: false,
			loading: false,
			error: null,
			pdfDoc: null,
			pdfLoaded: false,
			currentPage: 1,
			numPages: 0,
			pageInput: 1,
			resizeTimeout: null,
			errorSupportsRetries: true,
			errorTitle: "Error Loading PDF",
			currentRenderTask: null,
			currentLoadingTask: null,
			currentPdfUrl: null,
			isRendering: false,
			hasInitialRender: false,
			scale: 1.0,
			zoomLevel: 1.0,
			// Touch controls
			touchStartX: 0,
			touchStartY: 0,
			touchStartTime: 0,
			isSwiping: false
		};
	},
	watch: {
		visible: {
			immediate: true,
			handler(newVal) {
				this.isVisible = newVal;
				if (newVal && this.pdfUrl) {
					this.debouncedLoadPDF();
				}
			}
		},
		isVisible: {
			handler(newVal) {
				// If the dialog was closed (e.g., via ESC key) but the parent still thinks it's open
				if (!newVal && this.visible) {
					this.$emit('close');
				}
			}
		},
		pdfUrl: {
			handler(newVal) {
				if (newVal && this.isVisible) {
					this.debouncedLoadPDF();
				}
			}
		},
		currentPage: {
			handler(newVal) {
				this.pageInput = newVal;
			}
		}
	},
	methods: {
		// Cancel any ongoing PDF loading task to prevent race conditions
		cancelCurrentLoadingTask() {
			if (this.currentLoadingTask?._worker && !this.currentLoadingTask?._worker.destroyed) {
				this.currentLoadingTask._worker.destroy()
			}
			if (!this.currentLoadingTask?.destroyed && this.currentLoadingTask) {
				this.currentLoadingTask.destroy()
			}
		},

		// Debounce PDF loading to prevent multiple rapid calls
		debouncedLoadPDF() {
			if (this.loadPdfTimeout) {
				clearTimeout(this.loadPdfTimeout);
			}
			
			this.loadPdfTimeout = setTimeout(() => {
				this.loadPDF();
			}, 100);
		},

		// Load and display a PDF document
		async loadPDF() {
			if (!this.pdfUrl) {
				this.error = 'No PDF URL provided';
				return;
			}

			// Cancel any ongoing loading to prevent race conditions
			this.cancelCurrentLoadingTask();

			// Clean up previous PDF document
			if (this.pdfDoc) {
				try {
					this.pdfDoc.destroy();
				} catch (err) {
					// Ignore cleanup errors
				}
				this.pdfDoc = null;
			}

			// Store current URL to validate later (prevents wrong PDF from displaying)
			this.currentPdfUrl = this.pdfUrl;
			this.loading = true;
			this.error = null;
			this.pdfLoaded = false;
			this.hasInitialRender = false;
			this.zoomLevel = 1.0;

			try {
				// Create PDF.js loading task
				let loadingTask = getDocument({
					url: this.pdfUrl,
					withCredentials: false
				});
				
				this.currentLoadingTask = loadingTask;
				
				this.pdfDoc = await loadingTask.promise

				// Validate this is still the PDF we want (user may have switched)
				if (this.currentPdfUrl !== this.pdfUrl) {
					if (this.pdfDoc) {
						this.pdfDoc.destroy();
						this.pdfDoc = null;
					}
					return;
				}

				// PDF loaded successfully - update state
				this.currentLoadingTask = null;
				this.numPages = this.pdfDoc.numPages;
				this.currentPage = 1;
				this.pageInput = 1;
				this.loading = false;
				this.pdfLoaded = true;

				// Wait for next tick to ensure DOM is updated
				await this.$nextTick();
								
				// Render the first page
				await this.renderPage(1);
			} catch (err) {
				Sentry.captureException(err, { 
					tags: {
						component: "PDFViewerDialog",
						action: "loadPDF",
						pdfUrl: this.pdfUrl,
						fileName: this.fileName
					}
				});
				
				// Check if this is a password-protected PDF
				if (err.name === 'PasswordException') {
					this.error = 'This PDF is password protected and cannot be opened. Password-protected PDFs are not supported. Please download the PDF to view it.';
					this.errorTitle = "Password Protected PDF";
					this.errorSupportsRetries = false;
					return;
				} 
				// Handle specific worker-related errors
				if (err.message && (err.message.includes('WorkerMessageHandler') || err.message.includes('worker') || err.message.includes('is not a function') || err.message.includes('is undefined'))) {
					this.error = 'PDF viewing is not supported on this browser. Please download the PDF to view it.';
					this.errorTitle = "Browser Not Supported";
					this.errorSupportsRetries = false;
				}
				// Provide more specific error messages for other cases
				else if (err.message && (err.message.includes('CORS') || err.message.includes('cross-origin'))) {
					this.error = 'LiveSwitch was unable to load the PDF file. Please contact support if the issue persists.';
				} else if (err.message && (err.message.includes('Failed to fetch') || err.message.includes('network'))) {
					this.error = 'Network error loading PDF. This may be due to connectivity issues on your device.';
				} else if (err.message && (err.message.includes('Invalid') || err.message.includes('PDF'))) {
					this.error = 'The PDF file appears to be corrupted or invalid.';
				} else if (err.message && err.message.includes('HTTP 403')) {
					this.error = 'Access denied. The PDF URL may have expired or you may not have permission to view this file.';
				} else if (err.message && err.message.includes('HTTP 404')) {
					this.error = 'PDF file not found. The file may have been moved or deleted.';
				} else {
					this.error = `Failed to load PDF`;
				}
				this.error += ` Please contact support (${window.env.VITE_SUPPORT_EMAIL}) if the issue persists.`;
			} finally {
				this.loading = false;
				this.currentLoadingTask = null;
			}
		},

		// Render a specific page of the PDF to the canvas
		async renderPage(pageNum, forceRender = false) {
			if (!this.pdfDoc || !this.$refs.pdfCanvas || !this.$refs.canvasContainer) {
				return;
			}

			// Skip if already on this page and not forcing a re-render, but only if we've had an initial render
			if (!forceRender && this.currentPage === pageNum && !this.isRendering && this.hasInitialRender) {
				return;
			}

			// Cancel any ongoing render operation
			if (this.currentRenderTask) {
				try {
					this.currentRenderTask.cancel();
				} catch (err) {
					// Ignore cancellation errors
				}
				this.currentRenderTask = null;
			}

			// If already rendering, wait a bit and try again
			if (this.isRendering) {
				await new Promise(resolve => setTimeout(resolve, 50));
			}

			this.isRendering = true;

			try {
				const page = await this.pdfDoc.getPage(pageNum);
				const canvas = this.$refs.pdfCanvas;
				const context = canvas.getContext('2d');
				const container = this.$refs.canvasContainer;

				// Calculate optimal scale to fit page in container
				const containerWidth = container.clientWidth;
				const containerHeight = container.clientHeight;

				// Get PDF page dimensions at scale 1.0
				const viewport = page.getViewport({ scale: 1.0 });

				// Calculate the scale to fit the page in the container
				const scaleX = containerWidth / viewport.width;
				const scaleY = containerHeight / viewport.height;
				const baseScale = Math.min(scaleX, scaleY);
				
				// Apply zoom level multiplier on top of base scale
				const finalScale = baseScale * this.zoomLevel;

				// Get the final viewport with the calculated scale
				const scaledViewport = page.getViewport({ scale: finalScale });
				
				// Set canvas dimensions to match the scaled PDF size exactly
				canvas.width = scaledViewport.width;
				canvas.height = scaledViewport.height;

				// Clear canvas
				context.clearRect(0, 0, canvas.width, canvas.height);

				// Render the page at the exact scale
				const renderContext = {
					canvasContext: context,
					viewport: scaledViewport
				};

				// Store the render task so we can cancel it if needed
				this.currentRenderTask = page.render(renderContext);
				
				await this.currentRenderTask.promise;
				
				// Clear the render task since it completed successfully
				this.currentRenderTask = null;
				this.currentPage = pageNum;
				// Release page resources.
				page.cleanup();

				// Set hasInitialRender flag after the first successful render
				this.hasInitialRender = true;
			} catch (err) {
				// Clear the render task
				this.currentRenderTask = null;
				
				// Don't show error for cancelled operations
				if (err.name === 'RenderingCancelledException') {
					return;
				}
				
				Sentry.captureException(err, { 
					tags: {
						component: "PDFViewerDialog",
						action: "renderPage",
						pdfUrl: this.pdfUrl,
						fileName: this.fileName
					}
				});
				this.error = `Failed to render page ${pageNum}. Please contact support (${window.env.VITE_SUPPORT_EMAIL}) if the issue persists.`;
			} finally {
				this.isRendering = false;
			}
		},

		async goToNextPage() {
			if (this.currentPage < this.numPages) {
				await this.renderPage(this.currentPage + 1);
			}
		},

		async goToPreviousPage() {
			if (this.currentPage > 1) {
				await this.renderPage(this.currentPage - 1);
			}
		},

		async goToPage() {
			const page = parseInt(this.pageInput);
			if (page >= 1 && page <= this.numPages && page !== this.currentPage) {
				await this.renderPage(page);
			} else {
				// Reset input to current page if invalid
				this.pageInput = this.currentPage;
			}
		},

		async zoomIn() {
			if (this.zoomLevel < 3) {
				this.zoomLevel += 0.25;
				await this.renderPage(this.currentPage, true);
			}
		},

		async zoomOut() {
			if (this.zoomLevel > 0.5) {
				this.zoomLevel -= 0.25;
				await this.renderPage(this.currentPage, true);
			}
		},

		async resetZoom() {
			this.zoomLevel = 1.0;
			await this.renderPage(this.currentPage, true);
		},

		downloadPDF() {
			if (this.pdfUrl) {
				downloadFile(this.pdfUrl, this.fileName || 'document.pdf');
			}
		},

		closeDialog() {
			this.cancelCurrentLoadingTask();
			this.isVisible = false;
			this.pdfLoaded = false;
			this.$emit('close');
		},

		// Handle window resize - reset zoom and re-render current page
		async handleResize() {
			if (this.pdfLoaded && this.currentPage) {
				this.zoomLevel = 1.0;
				// Debounce resize events
				clearTimeout(this.resizeTimeout);
				this.resizeTimeout = setTimeout(async () => {
					await this.renderPage(this.currentPage, true); // Force render on resize
				}, 150);
			}
		},
		// Handle keyboard navigation
		handleKeyDown(event) {
			if (event.key === 'ArrowRight') {
				this.goToNextPage();
			} else if (event.key === 'ArrowLeft') {
				this.goToPreviousPage();
			} else if (event.key === 'ArrowUp') {
				this.goToPreviousPage();
			} else if (event.key === 'ArrowDown') {
				this.goToNextPage();
			}
		},
		// Touch event handlers for swipe navigation
		handleTouchStart(event) {
			if (!this.pdfLoaded || event.touches.length !== 1) return;
			
			// Don't handle touches that start on buttons or controls
			const target = event.target;
			if (target.closest('.v-btn') || target.closest('.floating-zoom-controls')) {
				return;
			}
			
			const touch = event.touches[0];
			this.touchStartX = touch.clientX;
			this.touchStartY = touch.clientY;
			this.touchStartTime = Date.now();
			this.isSwiping = false;
		},

		handleTouchMove(event) {
			if (!this.pdfLoaded || event.touches.length !== 1) return;
			
			// Don't handle touches that started on buttons
			if (this.touchStartX === 0 && this.touchStartY === 0) return;
			
			const touch = event.touches[0];
			const deltaX = Math.abs(touch.clientX - this.touchStartX);
			const deltaY = Math.abs(touch.clientY - this.touchStartY);
			
			// When zoomed in, be much more restrictive about swipe detection
			// Users are likely trying to pan around the document, not change pages
			if (this.zoomLevel > 1.0) {
				// For zoomed content, only consider it a swipe if it's a very fast, long horizontal gesture
				// and we're at the edge of the scrollable area
				const container = this.$refs.canvasContainer;
				if (container) {
					const isAtLeftEdge = container.scrollLeft <= 5;
					const isAtRightEdge = container.scrollLeft >= (container.scrollWidth - container.clientWidth - 5);
					
					// Only allow swipes when at edges and with much stricter requirements
					if ((isAtLeftEdge || isAtRightEdge) && deltaX > deltaY && deltaX > 80) {
						this.isSwiping = true;
						// Only prevent default if the event is cancelable
						if (event.cancelable) {
							event.preventDefault();
						}
					}
				}
			} else {
				// Normal swipe detection when not zoomed
				if (deltaX > deltaY && deltaX > 20) {
					this.isSwiping = true;
					// Only prevent default if the event is cancelable
					if (event.cancelable) {
						event.preventDefault();
					}
				}
			}
		},

		handleTouchEnd(event) {
			if (!this.pdfLoaded || this.isSwiping === false) return;
			
			// Don't handle touches that started on buttons
			if (this.touchStartX === 0 && this.touchStartY === 0) return;
			
			const touch = event.changedTouches[0];
			const deltaX = touch.clientX - this.touchStartX;
			const deltaY = Math.abs(touch.clientY - this.touchStartY);
			const deltaTime = Date.now() - this.touchStartTime;
			
			let swipeThreshold = 50;
			let timeThreshold = 500;
			
			// When zoomed, require much more deliberate swipes
			if (this.zoomLevel > 1.0) {
				swipeThreshold = 120; // Much longer swipe required
				timeThreshold = 300;  // Faster swipe required
			}
			
			// Check if it's a valid swipe with appropriate thresholds
			if (Math.abs(deltaX) > swipeThreshold && deltaY < 100 && deltaTime < timeThreshold && Math.abs(deltaX) > deltaY) {
				const container = this.$refs.canvasContainer;
				if (container) {
					const isAtLeftEdge = container.scrollLeft <= 5;
					const isAtRightEdge = container.scrollLeft >= (container.scrollWidth - container.clientWidth - 5);
					
					// When zoomed, only allow page changes when at appropriate edges
					if (this.zoomLevel > 1.0) {
						if (deltaX > 0 && isAtLeftEdge) {
							// Swipe right at left edge - go to previous page
							this.goToPreviousPage();
						} else if (deltaX < 0 && isAtRightEdge) {
							// Swipe left at right edge - go to next page
							this.goToNextPage();
						}
					} else {
						// Normal behavior when not zoomed
						if (deltaX > 0) {
							this.goToPreviousPage();
						} else {
							this.goToNextPage();
						}
					}
				}
			}
			
			// Reset swipe tracking
			this.isSwiping = false;
			this.touchStartX = 0;
			this.touchStartY = 0;
		},
	},

	mounted() {
		window.addEventListener('resize', this.handleResize);
		window.addEventListener('keydown', this.handleKeyDown);
	},

	// Cleanup when component is destroyed
	beforeDestroy() {
		window.removeEventListener('resize', this.handleResize);
		window.removeEventListener('keydown', this.handleKeyDown);
		
		// Clear any pending timeouts
		if (this.resizeTimeout) {
			clearTimeout(this.resizeTimeout);
		}
		
		// Cancel any ongoing render operation
		if (this.currentRenderTask) {
			try {
				this.currentRenderTask.cancel();
			} catch (err) {
				// Ignore cancellation errors
			}
			this.currentRenderTask = null;
		}
		
		// Cancel any ongoing loading task
		this.cancelCurrentLoadingTask();
		
		// Clean up PDF document
		if (this.pdfDoc) {
			this.pdfDoc.destroy();
		}
	}
};
</script>

<style scoped>
.pdf-canvas-container {
	background-color: #f9f9f9;
	border: 1px solid #e0e0e0;
	-webkit-overflow-scrolling: touch;
	scroll-behavior: smooth;
}

.canvas-wrapper {
	min-height: calc(100% - 20px);
	width: max-content;
	margin: 0 auto;
	min-width: calc(100% - 20px);
}

.pdf-canvas {
	min-width: 100px;
	min-height: 100px;
	max-width: none !important;
	max-height: none !important;
	width: auto !important;
	height: auto !important;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	background-color: #ffffff;
	display: block;
}

.zoom-btn {
	opacity: 0.7;
	transition: opacity 0.2s ease;
	min-width: 48px !important;
	min-height: 48px !important;
}

.zoom-btn:hover {
	opacity: 1;
}
</style>